/**
 * Earnings Retention Bonus, for display.
 *
 * ERB is minted onto the asset as a metadata trait and arrives as the raw string — `"2%"`,
 * `"0.5%"`, `"10%"` — so everything here is defensive about the format rather than assuming a
 * number. An unparseable value still shows; it just gets the base tier.
 *
 * What it does, from `GameSession`: the service fee on kill earnings is 20%, and your ERB is
 * *subtracted* from it. Bonuses sum across your equipped operative and weapons and are capped at
 * 15% total (`nft.models.NFTManager.calculate_erb`), so the fee floor is 5%. A single item's
 * number is therefore a share of a 15% budget, not a standalone rate — which is why the tooltip
 * says "stacks" and why the thresholds below are set where they are.
 */

/** Every ERB value that exists across the 15,000 minted weapons, and how many carry it:
 *
 *   0.5%  1490   1%  4819   1.5%  1490   2%  5520   3%  350   4%  1050   5%  280   10%  1
 *
 * So `>= 3%` is the top 11% of items and `>= 5%` is the top 1.9%. The tiers are those two cuts,
 * rather than round numbers picked because they look tidy. */
export const ERB_TIER_HIGH = 5
export const ERB_TIER_MID = 3

/** The cap on the *sum* of every equipped item's ERB. */
export const ERB_MAX_TOTAL = 15

export type ErbTier = 'high' | 'mid' | 'base'

/** The number inside an ERB trait string, or `null` when there isn't one. */
export const parseErb = (value?: string | null): number | null => {
    if (!value) return null
    const parsed = Number(String(value).replace(/[^0-9.]/g, ''))
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null
}

export const erbTier = (value?: string | null): ErbTier => {
    const parsed = parseErb(value)
    if (parsed === null) return 'base'
    if (parsed >= ERB_TIER_HIGH) return 'high'
    if (parsed >= ERB_TIER_MID) return 'mid'
    return 'base'
}

/**
 * Tier colours.
 *
 * Not invented for this badge: green already means "earnings bonus" on the loot box reveal card,
 * and `#FFE500` already means "rare enough to call out" on the Inventory page's legendary mark.
 * Two established meanings, reused rather than competed with.
 *
 * The hue lives in the border and the text; the *fill* is that hue already composited over black
 * rather than a low-alpha wash of it. A wash is only as dark as whatever it sits on, and this pill
 * sits on the item artwork — which on a fair number of the NFTs is a bright yellow, pink or green
 * studio backdrop, i.e. exactly the colours the text is drawn in. Keeping the fill dark and near
 * opaque means the contrast is a property of the badge instead of a property of the item behind it.
 */
export const ERB_TONE: Record<ErbTier, { className: string; glow?: string }> = {
    high: {
        className: 'border-[#FFE500]/70 bg-[#1A1700]/90 text-[#FFE500]',
        glow: '0 0 12px rgba(255,229,0,0.35)',
    },
    mid: {
        className: 'border-[#00FF90]/65 bg-[#001A0F]/90 text-[#00FF90]',
    },
    base: {
        className: 'border-white/25 bg-black/85 text-white/80',
    },
}

/** What the badge says on hover, and to a screen reader. */
export const erbDescription = (value: string) =>
    `Earnings Retention Bonus ${value} — cuts the 20% service fee on your kill earnings by that much. ` +
    `Stacks with your other equipped NFTs, up to ${ERB_MAX_TOTAL}% total.`
